'use client';

import { alpha, Box, IconButton, Modal, styled } from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { useId } from "react";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import MultiImageView from "@/app/features/shared/media-view/ui/shared/modal/MultiImageView";
import SingleImageView from "@/app/features/shared/media-view/ui/shared/modal/SingleImageView";

export interface ImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    image: MediaRef;
    relatedImages?: MediaRef[];
    magnifierEnabled?: boolean;
    imageInfoEnabled?: boolean;
}

export default function ImageModal({
    isOpen,
    onClose,
    image,
    relatedImages,
    magnifierEnabled = true,
    imageInfoEnabled = false,
}: ImageModalProps) {
    const titleId = useId();
    const descriptionId = useId();
    const hasRelatedImages = Boolean(relatedImages?.length);
    const imageKey = mediaRefKey(image);

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    padding: 4,
                    outline: 0,
                }}
            >
                <VisuallyHiddenHeading id={titleId}>
                    {hasRelatedImages ? 'Bildergalerie' : 'Bildansicht'}
                </VisuallyHiddenHeading>
                <VisuallyHiddenParagraph id={descriptionId}>
                    {hasRelatedImages
                        ? 'Vergrößerte Bildergalerie. Mit den Pfeiltasten nach links und rechts kann zwischen Bildern gewechselt werden.'
                        : 'Vergrößerte Einzelbildansicht.'}
                </VisuallyHiddenParagraph>
                {hasRelatedImages
                    ? <MultiImageView key={`multi-${imageKey}`} image={image} relatedImages={relatedImages}/>
                    : (
                        <SingleImageView
                            key={`single-${imageKey}`}
                            image={image}
                            magnifierEnabled={magnifierEnabled}
                            imageInfoEnabled={imageInfoEnabled}
                        />
                    )}
                <CloseButton autoFocus onClick={onClose} aria-label="Bildansicht schließen">
                    <Cancel/>
                </CloseButton>
            </Box>
        </Modal>
    )
}

function mediaRefKey(image: MediaRef): string {
    if (typeof image === 'string') return image;
    if ('file' in image) {
        return [
            'local',
            image.name ?? image.file.name,
            image.file.size,
            image.file.lastModified,
        ].join(':');
    }

    return [
        'upload',
        image.documentId,
        image.url,
        image.name,
    ].filter(Boolean).join(':');
}

const visuallyHiddenStyles = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
} as const;

const VisuallyHiddenHeading = styled('h2')(visuallyHiddenStyles);
const VisuallyHiddenParagraph = styled('p')(visuallyHiddenStyles);

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    transform: 'translate(-50%, 50%)',
    color: theme.palette.common.black,
    backgroundColor: alpha(theme.palette.common.white, .8),
    border: `1px solid ${alpha(theme.palette.common.black, .5)}`,
    '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.common.white, .9),
    }
}));
